import React, { useCallback, useMemo } from "react";
import { Form as OgForm, Formik as OgFormik, FormikFormProps } from "formik";
import {
  FormikConfig,
  FormikHelpers,
  FormikProps,
  FormikValues
} from "formik/dist/types";
import { defaultSubmitTracker, NeedsNameTracker } from "./analytics";
import { datadogLogs } from "@datadog/browser-logs";
import { Omit } from "generated/graphql";
import { fromSchema, SchemaFields } from "./Typesafe";
import { Schema } from "yup";
import { DimensionValues } from "monitoring";

export type NonNullableRequired<T> = NonNullable<
  {
    [P in keyof Required<T>]: NonNullable<T[P]>;
  }
>;
/**
 * Merge with the Formik children function props to give access to the default field configs.
 */
export type FieldFactory<Values> = {
  field: SchemaFields<Values>;
};

/**
 * Our overrides for the default Formik props.
 * The overrides are compatible with base Formik, but not all base Formik apply here.
 * See `Formik` component for more details.
 */
export type TypeSafeForm<Values extends FormikValues> = {
  initialValues: NonNullableRequired<Values>;
  validationSchema: Schema<Values>;
  children?:
    | ((props: FormikProps<Values> & FieldFactory<Values>) => React.ReactNode)
    | React.ReactNode;
};
export type FromForkmik<T> = Omit<
  FormikConfig<T>,
  "validationSchema" | "children" | "render" | "initialValues"
>;
/**
 * The props needed to name and track forms.
 */
export type NamedForm = {
  name: string;
  tracker?: NeedsNameTracker | null;
  customDimensions?: Partial<
    Pick<DimensionValues, "model" | "software_version">
  >;
};

/**
 *  Builds a type safe form.
 *  The same as Formik's forms except for the following.
 *  * Validation Schema is required
 *  * Initial values cannot have any nulls (source of bugs https://jaredpalmer.com/formik/docs/api/formik#initialvalues-values) "Even if your form is empty by default, you must initialize all fields with initial values otherwise React will throw an error saying that you have changed an input from uncontrolled to controlled."
 *  * The deprecated `render` prop has been removed.
 *  * Default field configurations are available to the children render function.
 *
 *  To use the TypeSafe field props simply just use the child render function.
 *  The type for fields is automatically picked up from the schema.
 *
 *   const schema = yup.object({
 *    a: yup.string()
 *  });
 *  <Formik
 *     onSubmit={onSubmit}
 *     initialValues={initialValues}
 *     name="testForm"
 *     tracker={tracker}
 *     validationSchema={schema}
 *     >
 *     {({field}) => (
 *              <Form>
 *                <label htmlFor="a">
 *                  a
 *                  <Field type="text" {...field.a} />
 *                </label>
 *                <SubmitButton
 *                  labelKey="SUBMIT"
 *                  submittingLabelKey="SUBMITTING"
 *                />
 *              </Form>
 *            )}
 *  </Formik>
 *
 * @param initialValues The initial form values
 * @param onSubmit Your submit handler
 * @param name The name of this form
 * @param children the children render function/fields described https://jaredpalmer.com/formik/docs/api/formik#children-reactreactnode--props-formikpropsvalues--reactnode
 * @param tracker (Optional) A form fields tracker, all forms have a simple tracker by default, to disable tracking set to null  explicitly
 * @param validationSchema (optional) Used for validating the forms fields as well as reliably knowing their intended types at runtime.
 * @param rest The other formik props.
 * @constructor
 */
export function PolyFormik<
  Values extends FormikValues = FormikValues,
  ExtraProps extends {} = {}
>({
  initialValues,
  onSubmit,
  name,
  children,
  tracker = defaultSubmitTracker,
  validationSchema,
  customDimensions,
  ...rest
}: FromForkmik<Values> &
  ExtraProps &
  NamedForm &
  TypeSafeForm<Values>): JSX.Element {
  const submit = useCallback<FromForkmik<Values>["onSubmit"]>(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      if (tracker) {
        try {
          return tracker(name)(
            initialValues,
            onSubmit,
            validationSchema,
            customDimensions
          )(values, formikHelpers);
        } catch (e) {
          datadogLogs.logger.error("Failed to initialize tracker", { name });
        }
      }
      return onSubmit(values, formikHelpers);
    },
    [onSubmit, tracker, customDimensions, initialValues, name, validationSchema]
  );
  //fromSchema is a very expensive calculation for larger forms, and if this is not memooized it can be
  // recomputed any time a form prop changes.  eg: setValue, setError, etc.
  const field = useMemo(() => {
    return fromSchema<Values>(validationSchema);
  }, [validationSchema]);
  const withField = (props: FormikProps<Values>): React.ReactNode => {
    if (children && typeof children === "function") {
      const fieldProps: FieldFactory<Values> = {
        field
      };
      return children({ ...props, ...fieldProps });
    }
    return children;
  };
  return (
    <OgFormik<Values>
      {...rest}
      children={withField}
      onSubmit={submit}
      /* intialValues is only eer the non null version of values, so it can be assigned, but he ts compiler will get upset about this */
      initialValues={initialValues as Values}
      validationSchema={validationSchema}
    />
  );
}

export function Form(props: FormikFormProps) {
  return <OgForm {...props} />;
}

/**
 * Migrate to Formik with a validation schema.
 * @deprecated
 * @constructor
 */
export function UnsafeFormik<
  Values extends FormikValues = FormikValues,
  ExtraProps extends {} = {}
>({
  initialValues,
  onSubmit,
  name,
  tracker = defaultSubmitTracker,
  validationSchema,
  customDimensions,
  ...rest
}: Omit<FormikConfig<Values>, "render"> & ExtraProps & NamedForm): JSX.Element {
  let submit = onSubmit;
  if (tracker) {
    try {
      submit = tracker(name)(
        initialValues,
        onSubmit,
        validationSchema,
        customDimensions
      );
    } catch (e) {
      datadogLogs.logger.error("Failed to initialize tracker", { name });
    }
  }
  return (
    <OgFormik
      {...rest}
      onSubmit={submit}
      initialValues={initialValues}
      validationSchema={validationSchema}
    />
  );
}
